import * as React from 'react';

import {
  useConfig,
  useContractContext,
  useModalSettingsContext,
  usePostMessageContext,
  usePostMessageHandler,
} from 'src/providers';

import { CloseButton } from 'src/components/Modal/CloseButton';
import FAQ from 'src/components/Modal/FAQ';
import Logos from 'src/components/Modal/Logos';

import { Main } from './Main';

import { StyledSlideOut, StyledOverlay } from './StyledSlideOut';

export function SlideOut() {
  const { isGlobalCart } = useConfig();
  const { contracts, declineProtection } = useContractContext();
  const { modalType, isModalOpen, theme } = useModalSettingsContext();
  const { postMessage } = usePostMessageContext();

  const [activeTab, setActiveTab] = React.useState('main');
  const [customSlideOutWidth, setCustomSlideOutWidth] = React.useState<number | null>(null);
  const [modalEl, setModalEl] = React.useState<HTMLDivElement | null>(null);

  const setModalRef = React.useCallback((el: HTMLDivElement) => {
    setModalEl(el);
  }, [setModalEl]);

  const closeButtonRef: React.RefObject<HTMLButtonElement> = React.useRef(null);

  const faqClickHandler = React.useCallback(() => {
    postMessage('viewFaq', { ctaType: 'modal', modalType });
    setActiveTab('faq');
  }, [postMessage, setActiveTab, modalType]);

  const faqExpandHandler = React.useCallback((question: string, index: string) => {
    postMessage('faqQueryOpened', { ctaType: 'modal', modalType, question, index });
  }, [postMessage, modalType]);

  const renderTab = () => {
    switch (activeTab) {
      case 'faq':
        return <FAQ onBackRequested={() => setActiveTab('main')} onFaqExpanded={faqExpandHandler} />;
      case 'main':
      default:
        return <Main closeButtonRef={closeButtonRef} onFAQClicked={faqClickHandler} />;
    }
  };

  usePostMessageHandler('setFocusTrap', React.useCallback(() => {
    if (activeTab === 'main') {
      closeButtonRef?.current?.focus();
    }
  }, [activeTab]));

  type MerchantCartDrawerSizeChangedEventDetailType = { height: number; width: number; };
  usePostMessageHandler('merchantCartDrawerSizeChanged', React.useCallback((e: Event) => {
    const { detail } = (e as CustomEvent<MerchantCartDrawerSizeChangedEventDetailType>);
    setCustomSlideOutWidth(detail.width);
  }, [setCustomSlideOutWidth]));

  const handleOverlayClick = React.useCallback(() => {
    declineProtection('overlay-click', modalType);
  }, [modalType, declineProtection]);

  const getFocusableElements = () => {
    if (!modalEl) {
      return [];
    }
    const focusableChildren = modalEl.querySelectorAll('a, button, [tabindex="0"]');
    return Array.prototype.slice.call(focusableChildren);
  };

  React.useEffect(() => {
    const focusableElements = getFocusableElements();
    if (focusableElements.length) {
      focusableElements[0].focus();
    }
  }, []);

  const manageFocus = React.useCallback((e: React.KeyboardEvent) => {
    if (e.keyCode === 27) { // Escape
      declineProtection('esc_key', modalType);
    }

    if (!modalEl || e.key !== 'Tab') {
      return;
    }

    const focusableElements = getFocusableElements();
    if (!focusableElements.length) {
      return;
    }

    const firstTabStop = focusableElements[0];
    const lastTabStop = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === focusableElements[0]) {
        e.preventDefault();
        lastTabStop.focus();
      }
    } else {
      if (document.activeElement === lastTabStop) {
        e.preventDefault();
        firstTabStop.focus();
      }
    }
  }, [modalEl, modalType, declineProtection]);

  const renderOverlay = () => {
    if (isModalOpen && !isGlobalCart) {
      // If global cart is not implemented, render animated darkened overlay
      return (
        <StyledOverlay
          aria-hidden="true"
          className="overlay"
          onClick={handleOverlayClick}
        />
      );
    }

    return <div aria-hidden="true" className="overlay" onClick={handleOverlayClick} />;
  };

  if (!contracts.length) {
    return null;
  }

  return (
    <StyledSlideOut
      aria-label="Protect your purchase"
      aria-modal="true"
      className="clyde-slide-in"
      onKeyDown={manageFocus}
      role="dialog"
      tabIndex={-1}
      width={customSlideOutWidth}
    >
      {renderOverlay()}
      <div className="content" ref={setModalRef}>
        <CloseButton ref={closeButtonRef} />

        <Logos
          showXCoverBranding={theme.showXCoverBranding}
          partnerLogo={theme.partnerLogo}
          shopName={theme.shopName}
          onLoad={() => null}
        />

        {renderTab()}
      </div>
    </StyledSlideOut>
  );
}
